import { Head, Link } from '@inertiajs/react';
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  GitBranch,
  Video,
  XCircle,
} from 'lucide-react';
import { AvatarCircle } from '@/components/admin/avatar-circle';
import { githubUsername } from '@/components/admin/github-username';
import { SubmissionStatusBadge } from '@/components/admin/submission-status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AdminTeamsShow } from '@/types';
import { AddHackerDialog } from './components/AddHackerDialog';
import { AssignMentorDialog } from './components/AssignMentorDialog';
import { DeleteTeamButton } from './components/DeleteTeamButton';
import { RemoveHackerButton } from './components/RemoveHackerButton';

const VISIBLE_STACK_AVATARS = 5;

function formatDateTime(timestamp: string) {
  return new Date(timestamp).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export default function Show({
  team,
  mentors,
  availableHackers,
  presentationUpload,
  eventSlug,
  hackerSearch,
  title,
}: AdminTeamsShow) {
  const overflowCount = team.members.length - VISIBLE_STACK_AVATARS;

  return (
    <>
      <Head title={title} />
      <div className="container mx-auto space-y-6 py-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link href={`/admin/${eventSlug}/teams`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Teams
            </Link>
          </Button>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {team.members.length > 0 && (
              <div className="flex shrink-0 -space-x-3">
                {team.members.slice(0, VISIBLE_STACK_AVATARS).map((member) => (
                  <AvatarCircle
                    key={member.id}
                    src={member.avatarUrl}
                    name={member.fullName}
                    className="ring-2 ring-background"
                  />
                ))}
                {overflowCount > 0 && (
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground text-xs ring-2 ring-background">
                    +{overflowCount}
                  </div>
                )}
              </div>
            )}
            <div>
              <h1 className="font-bold text-3xl tracking-tight">
                {team.slug}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {team.track && (
                  <Link href={`/admin/${eventSlug}/tracks/${team.track.slug}`}>
                    <Badge className="cursor-pointer transition-opacity hover:opacity-80">
                      {team.track.name}
                    </Badge>
                  </Link>
                )}
                {team.mentor && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1.5 py-0.5 pr-2.5 pl-1"
                  >
                    <AvatarCircle
                      src={team.mentor.avatarUrl}
                      name={team.mentor.fullName}
                      size="sm"
                      className="h-4 w-4 ring-0"
                    />
                    Mentor: {team.mentor.fullName}
                  </Badge>
                )}
                {team.formedOnSite && (
                  <Badge variant="secondary">Formed on-site</Badge>
                )}
                {team.tableNumber && (
                  <Badge variant="outline">Table {team.tableNumber}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AssignMentorDialog
              eventSlug={eventSlug}
              teamId={team.id}
              currentMentorId={team.mentorId}
              mentors={mentors}
            />
            <AddHackerDialog
              eventSlug={eventSlug}
              teamId={team.id}
              teamSlug={team.slug}
              hackerSearch={hackerSearch}
              availableHackers={availableHackers}
            />
            <DeleteTeamButton
              eventSlug={eventSlug}
              teamId={team.id}
              teamSlug={team.slug}
              memberCount={team.members.length}
              hasProject={team.project !== null}
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Team Members ({team.members.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {team.members.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <p>No members in this team yet.</p>
                <p className="mt-2 text-sm">
                  Click &quot;Add Hacker&quot; to add members.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {team.members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between gap-4 rounded-lg border p-4"
                  >
                    <Link
                      href={`/admin/${eventSlug}/${githubUsername(member.github)}`}
                      className="group flex min-w-0 items-center gap-3"
                    >
                      <AvatarCircle src={member.avatarUrl} name={member.fullName} />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex items-center gap-2">
                          <span className="font-medium group-hover:underline">
                            {member.fullName}
                          </span>
                          {!member.entered && (
                            <Badge
                              variant="outline"
                              className="border-amber-300 bg-amber-100 text-amber-800"
                            >
                              No ha entrado
                            </Badge>
                          )}
                        </span>
                        <span className="truncate text-muted-foreground text-sm">
                          {member.email}
                        </span>
                      </div>
                    </Link>
                    <div className="flex shrink-0 items-center gap-3">
                      {member.github && (
                        <a
                          href={member.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
                        >
                          @{githubUsername(member.github)}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <RemoveHackerButton
                        eventSlug={eventSlug}
                        hackerProfileId={member.id}
                        hackerName={member.fullName}
                        teamId={team.id}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Submission</CardTitle>
          </CardHeader>
          <CardContent>
            {team.project ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{team.project.name}</p>
                    {team.project.oneliner && (
                      <p className="text-muted-foreground text-sm">
                        {team.project.oneliner}
                      </p>
                    )}
                  </div>
                  <SubmissionStatusBadge status={team.project.submissionStatus} />
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  {team.project.repoUrl && (
                    <a
                      href={team.project.repoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                    >
                      <GitBranch className="h-4 w-4" />
                      Repository
                    </a>
                  )}
                  {team.project.videoUrl && (
                    <a
                      href={team.project.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                    >
                      <Video className="h-4 w-4" />
                      Video
                    </a>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  Last updated {formatDateTime(team.project.updatedAt)}
                </p>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <p>No project submitted yet.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Presentation Uploads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  {presentationUpload?.slidesUploadedAt ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">Slides (PDF)</p>
                    {presentationUpload?.slidesUploadedAt ? (
                      <p className="text-muted-foreground text-sm">
                        Uploaded on{' '}
                        {formatDateTime(presentationUpload.slidesUploadedAt)}
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        Not uploaded yet
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  {presentationUpload?.demoUploadedAt ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">Demo Video</p>
                    {presentationUpload?.demoUploadedAt ? (
                      <p className="text-muted-foreground text-sm">
                        Uploaded on{' '}
                        {formatDateTime(presentationUpload.demoUploadedAt)}
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        Not uploaded yet
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
